import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@apollo/client/react';
import type { Feature } from 'geojson';
import * as R from 'ramda';

import { GET_KML_LAYERS } from '@atom/graph/kml';
import { useNavigation } from '@atom/hooks/useNavigation';
import type { FeatureClickHandler, OverlayLayer } from '@atom/map';
import { KmlClientType, KmlLayer } from '@atom/types/map';

import { useMapLayers } from '../MapPortalDataProvider';

import { fetchKmlGeoJson } from './kmlConvert';
import { measureIconSizes } from './kmlIconSizes';
import { kmlSymbology } from './kmlSymbology';
import type { LayerData, SelectedPlacemark } from './kmlTypes';

const KML_POPUP_FIELDS: readonly string[] = ['name', 'description'];

type KmlLayersContextValue = {
  kmlDictionary: KmlLayer[];
  layers: OverlayLayer[];
  selectedPlacemark: SelectedPlacemark | null;
  clearPlacemark: () => void;
  loading: boolean;
};

const KmlLayersContext = createContext<KmlLayersContextValue>({
  kmlDictionary: [],
  layers: [],
  selectedPlacemark: null,
  clearPlacemark: () => {},
  loading: false,
});

export const useKmlLayers = (): KmlLayersContextValue =>
  useContext(KmlLayersContext);

type KmlLayersProviderProps = {
  children: ReactNode;
};

const KmlLayersProvider = ({ children }: KmlLayersProviderProps) => {
  const [, { updateSearchParams }] = useNavigation();
  const {
    layers: { geospatialLayers: kmlLayersEnabled, kmlLayerIds },
  } = useMapLayers();

  const [kmlDictionary, setKmlDictionary] = useState<KmlLayer[]>([]);
  const [layerDataById, setLayerDataById] = useState<Record<string, LayerData>>(
    {},
  );
  const [selectedPlacemark, setSelectedPlacemark] =
    useState<SelectedPlacemark | null>(null);

  const loadedUrlsRef = useRef<Record<string, string>>({});
  const seededRef = useRef(false);
  const pollingIntervalsRef = useRef<
    Record<string, ReturnType<typeof setInterval>>
  >({});

  const { data: kmlLayersData, loading: queryLoading } = useQuery(
    GET_KML_LAYERS,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    },
  );

  useEffect(() => {
    if (seededRef.current || !kmlLayersData) {
      return;
    }

    seededRef.current = true;

    const filtered = kmlLayersData.kmlLayers
      .filter(
        layer =>
          layer.client === KmlClientType.WEB ||
          layer.client === KmlClientType.BOTH,
      )
      .map(layer =>
        layer.pollingInterval
          ? { ...layer, url: `${layer.url}?rev=${Date.now()}` }
          : layer,
      );
    setKmlDictionary(filtered);
  }, [kmlLayersData]);

  const selectedLayers = useMemo(
    () =>
      kmlLayersEnabled && kmlLayerIds.size > 0
        ? [...kmlLayerIds]
            .map(id => kmlDictionary.find(layer => layer.id === id))
            .filter((layer): layer is KmlLayer => Boolean(layer))
        : [],

    [kmlLayersEnabled, kmlLayerIds, kmlDictionary],
  );

  useEffect(() => {
    const pending = selectedLayers.filter(
      layer => loadedUrlsRef.current[layer.id] !== layer.url,
    );

    const loadLayer = async (layer: KmlLayer) => {
      loadedUrlsRef.current[layer.id] = layer.url;

      try {
        const features = await fetchKmlGeoJson(layer.url);
        // Measure each placemark icon's natural size so the symbology can draw
        // it at native dimensions × scale (see kmlSymbology) rather than a
        // fixed square, matching the legacy google.maps.KmlLayer footprint.
        const iconUrls = features.features.flatMap(feature => {
          const href = feature.properties?.icon;
          return typeof href === 'string' && href ? [href] : [];
        });
        const iconSizes = await measureIconSizes(iconUrls);
        setLayerDataById(prev => ({
          ...prev,
          [layer.id]: {
            features,
            symbology: kmlSymbology(layer.markerId, iconSizes),
          },
        }));
      } catch {
        // Clear the marker so the layer is retried on the next selection or
        // poll tick instead of being stuck unrendered on a transient failure.
        if (loadedUrlsRef.current[layer.id] === layer.url) {
          delete loadedUrlsRef.current[layer.id];
        }
      }
    };

    Promise.all(pending.map(loadLayer));

    loadedUrlsRef.current = R.pick(
      selectedLayers.map(layer => layer.id),
      loadedUrlsRef.current,
    );
  }, [selectedLayers]);

  useEffect(() => {
    const pollingLayers = selectedLayers.filter(layer =>
      Boolean(layer.pollingInterval),
    );

    // Add intervals for newly-selected polling layers.
    pollingLayers
      .filter(layer => !pollingIntervalsRef.current[layer.id])
      .forEach(layer => {
        pollingIntervalsRef.current[layer.id] = setInterval(() => {
          const baseUrl = layer.url.replace(/\?rev=\d+$/, '');
          setKmlDictionary(prev =>
            prev.map(entry =>
              entry.id === layer.id
                ? { ...entry, url: `${baseUrl}?rev=${Date.now()}` }
                : entry,
            ),
          );
        }, layer.pollingInterval);
      });

    const pollingIds = pollingLayers.map(layer => layer.id);
    const stale = R.omit(pollingIds, pollingIntervalsRef.current);

    R.forEachObjIndexed(intervalId => clearInterval(intervalId), stale);

    pollingIntervalsRef.current = R.pick(
      pollingIds,
      pollingIntervalsRef.current,
    );
  }, [selectedLayers]);

  useEffect(
    () => () => {
      R.forEachObjIndexed(
        intervalId => clearInterval(intervalId),
        pollingIntervalsRef.current,
      );
    },
    [],
  );

  const makeClickHandler = useCallback(
    (layer: KmlLayer): FeatureClickHandler =>
      (feature: Feature) => {
        setSelectedPlacemark({ layer, properties: feature.properties });
        updateSearchParams({ view: 'kml' });
      },
    [updateSearchParams],
  );

  const clearPlacemark = useCallback(() => {
    setSelectedPlacemark(null);
  }, []);

  const layers: OverlayLayer[] = useMemo(
    () =>
      selectedLayers.flatMap(layer => {
        const data = layerDataById[layer.id];
        if (!data) {
          return [];
        }

        return [
          {
            collection: data.features,
            symbology: data.symbology,
            fieldAliases: { name: 'Name' },
            popupFields: KML_POPUP_FIELDS,
            popupTitleField: 'name',
            popupHtmlField: 'description',
            onFeatureClick: layer.suppressInfoWindows
              ? makeClickHandler(layer)
              : null,
          },
        ];
      }),
    [selectedLayers, layerDataById, makeClickHandler],
  );

  const value = useMemo(
    () => ({
      kmlDictionary,
      layers,
      selectedPlacemark,
      clearPlacemark,
      loading: queryLoading,
    }),
    [kmlDictionary, layers, selectedPlacemark, clearPlacemark, queryLoading],
  );

  return (
    <KmlLayersContext.Provider value={value}>
      {children}
    </KmlLayersContext.Provider>
  );
};

export default KmlLayersProvider;
